import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import AuthShell, { GoogleButton } from "@/components/AuthShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";

export default function Login() {
  const { login, errMessage } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [needsVerify, setNeedsVerify] = useState(false);
  const [resending, setResending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setNeedsVerify(false);
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Welcome back!");
      navigate(location.state?.from || "/dashboard", { replace: true });
    } catch (err) {
      const msg = errMessage(err);
      if (err?.response?.status === 403 && /verify/i.test(msg)) {
        setNeedsVerify(true);
      }
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  const resend = async () => {
    setResending(true);
    try {
      await api.post("/auth/resend-verification", { email });
      toast.success("If this email needs verification, we've sent a new link.");
    } catch {
      toast.success("If this email needs verification, we've sent a new link.");
    } finally {
      setResending(false);
    }
  };

  return (
    <AuthShell
      title="Log in"
      subtitle="Access your saved reference pages and projects."
      footer={<>New to CiteQR? <Link to="/register" className="font-medium text-[#0D9488] hover:underline">Create an account</Link></>}
    >
      <form onSubmit={submit} className="space-y-4" noValidate>
        {error && <div role="alert" data-testid="login-error" className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>}
        {needsVerify && (
          <div className="rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-800" data-testid="login-needs-verify">
            Your email isn't verified yet.{" "}
            <button type="button" onClick={resend} disabled={resending} className="font-medium underline disabled:opacity-60" data-testid="resend-verification-btn">
              {resending ? "Sending…" : "Resend verification email"}
            </button>
          </div>
        )}
        <div className="space-y-1.5">
          <Label htmlFor="email">Email</Label>
          <Input id="email" type="email" autoComplete="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="login-email" />
        </div>
        <div className="space-y-1.5">
          <div className="flex items-center justify-between">
            <Label htmlFor="password">Password</Label>
            <Link to="/forgot-password" className="text-xs text-[#0D9488] hover:underline">Forgot password?</Link>
          </div>
          <Input id="password" type="password" autoComplete="current-password" required value={password} onChange={(e) => setPassword(e.target.value)} data-testid="login-password" />
        </div>
        <Button type="submit" disabled={loading} className="w-full bg-[#0A2540] text-white hover:bg-[#0c2e4e]" data-testid="login-submit">
          {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Log in
        </Button>
      </form>
      <div className="my-5 flex items-center gap-3">
        <Separator className="flex-1" />
        <span className="text-xs text-slate-400">OR</span>
        <Separator className="flex-1" />
      </div>
      <GoogleButton label="Log in with Google" />
    </AuthShell>
  );
}
